"use client";

import { useState, useEffect } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import axios from "axios";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

const VarianceModal = ({
  budgetId,
  lineItems,
  vendors,
  bidAreaOptions,
  projectId,
  onClose,
  setProject,
  onVarianceCreated,
  setDialog,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [varianceData, setVarianceData] = useState({});
  const [allVariances, setAllVariances] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalBudgetItems, setOriginalBudgetItems] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [newItemData, setNewItemData] = useState({});
  const [currentItems, setCurrentItems] = useState(lineItems);
  const [isProcessingNewItems, setIsProcessingNewItems] = useState(false);

  const currentItem = currentItems[currentIndex];
  const isLastItem =
    currentIndex === currentItems.length - 1 &&
    (!isProcessingNewItems || newItems.length === 0);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  useEffect(() => {
    const fetchOriginalBudgetItems = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const originalBudget = response.data.budgets.find(
          (b) => b._id === budgetId
        );
        setOriginalBudgetItems(originalBudget?.lineItems || []);
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to fetch original budget items: " + err.message,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    };
    fetchOriginalBudgetItems();
  }, [budgetId, projectId, setDialog]);

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleVarianceChange = (field, value) => {
    setVarianceData((prev) => ({
      ...prev,
      [field]:
        field === "requiredQty" || field === "quantity" ? Number(value) : value,
    }));
  };

  const handleNewItemChange = (field, value) => {
    setNewItemData((prev) => ({
      ...prev,
      [field]:
        field === "requiredQty" || field === "quantity" ? Number(value) : value,
    }));
  };

  const addNewItem = () => {
    if (!newItemData.selectedItem) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select an item to add.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const selectedItem = originalBudgetItems.find(
      (item) => item._id === newItemData.selectedItem
    );
    setNewItems((prev) => [
      ...prev,
      {
        ...selectedItem,
        varianceData: {
          ...newItemData,
          cost: Number(newItemData.cost || selectedItem.cost || 0).toFixed(2),
          price: Number(newItemData.price || selectedItem.price || 0).toFixed(
            2
          ),
          quantity: Number(newItemData.quantity || selectedItem.quantity || 0),
          requiredQty: Number(
            newItemData.requiredQty || selectedItem.requiredQty || 0
          ),
          unitRate: Number(
            newItemData.unitRate ||
              selectedItem.unitRate ||
              (selectedItem.quantity > 0
                ? selectedItem.amount / selectedItem.quantity
                : 0)
          ).toFixed(2),
          costCodeDescription:
            newItemData.costCodeDescription || selectedItem.costCodeDescription,
          uom: newItemData.uom || selectedItem.uom,
        },
      },
    ]);
    setNewItemData({});
    setShowNewItemForm(false);
  };

  const calculateDifferences = (item, variance) => {
    const isMaterial = item.category === "M";

    const oldCost = isMaterial
      ? Number(item.cost || 0)
      : Number(item.unitRate || item.cost || 0);

    const newCost = isMaterial
      ? Number(variance.cost ?? item.cost ?? 0)
      : Number(variance.unitRate ?? variance.cost ?? oldCost ?? 0);

    const oldPrice = Number(item.price || 0);
    const newPrice = Number(variance.price ?? oldPrice);

    const originalQuantity = isMaterial
      ? Number(item.requiredQty || 0)
      : Number(item.quantity || 0);

    const newQuantity = isMaterial
      ? Number(variance.requiredQty ?? originalQuantity)
      : Number(variance.quantity ?? originalQuantity);

    const quantityDiff = newQuantity - originalQuantity;

    // Critical: Use this helper to avoid floating-point errors
    const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

    const costDiffRaw =
      quantityDiff * newCost + (newCost - oldCost) * originalQuantity;
    const priceDiffRaw =
      quantityDiff * newPrice + (newPrice - oldPrice) * originalQuantity;

    // Always round to exactly 2 decimal places — same as backend
    const costDiff = roundToTwo(costDiffRaw);
    const priceDiff = roundToTwo(priceDiffRaw);

    return {
      quantityDiff,
      costDiff,
      priceDiff,
    };
  };

  const { quantityDiff, costDiff, priceDiff } = calculateDifferences(
    currentItem,
    varianceData
  );

  const handleNext = () => {
    if (!varianceData.varianceTitle?.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Variance Title is required.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    if (!varianceData.varianceDescription?.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Variance Reason is required.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (currentItem) {
      const differences = calculateDifferences(currentItem, varianceData);
      const currentCost = Number(
        varianceData.cost ||
          varianceData.unitRate ||
          currentItem.cost ||
          currentItem.unitRate ||
          0
      );
      const currentPrice = Number(varianceData.price || currentItem.price || 0);
      const isMaterial = currentItem.category === "M";
      const originalQuantity = isMaterial
        ? Number(currentItem.requiredQty || 0)
        : Number(currentItem.quantity || currentItem.requiredQty || 0);
      const oldCost = Number(currentItem.cost || currentItem.unitRate || 0);
      const oldPrice = Number(currentItem.price || 0);

      if (
        differences.quantityDiff !== 0 &&
        differences.costDiff === 0 &&
        currentCost !== 0
      ) {
        const expected =
          Number((differences.quantityDiff * currentCost).toFixed(2)) +
          (currentCost !== oldCost
            ? Number(((currentCost - oldCost) * originalQuantity).toFixed(2))
            : 0);
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: `Cost Difference must be provided. Expected ${formatNumber(
            expected
          )}`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      if (
        differences.quantityDiff !== 0 &&
        differences.priceDiff === 0 &&
        currentPrice !== 0
      ) {
        const expected =
          Number((differences.quantityDiff * currentPrice).toFixed(2)) +
          (currentPrice !== oldPrice
            ? Number(((currentPrice - oldPrice) * originalQuantity).toFixed(2))
            : 0);
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: `Price Difference must be provided. Expected ${formatNumber(
            expected
          )}`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      if (
        differences.quantityDiff === 0 &&
        currentPrice !== oldPrice &&
        differences.priceDiff === 0
      ) {
        const expected = Number(
          ((currentPrice - oldPrice) * originalQuantity).toFixed(2)
        );
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: `Price Difference must be provided. Expected ${formatNumber(
            expected
          )}`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      if (
        differences.quantityDiff === 0 &&
        currentCost !== oldCost &&
        differences.costDiff === 0
      ) {
        const expected = Number(
          ((currentCost - oldCost) * originalQuantity).toFixed(2)
        );
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: `Cost Difference must be provided. Expected ${formatNumber(
            expected
          )}`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const varianceEntry = {
        lineItemId: currentItem._id,
        updatedItem: {
          ...(currentItem.category === "L" && {
            costCodeDescription:
              varianceData.costCodeDescription ||
              currentItem.costCodeDescription ||
              "",
            uom: varianceData.uom || currentItem.uom || "",
            unitRate: Number(
              varianceData.unitRate ||
                currentItem.unitRate ||
                (currentItem.quantity > 0
                  ? currentItem.amount / currentItem.quantity
                  : 0)
            ).toFixed(2),
            quantity: Number(
              varianceData.quantity ?? currentItem.quantity ?? 0
            ),
            laborHours: Number(
              varianceData.laborHours ?? currentItem.laborHours ?? 0
            ),
            price: Number(varianceData.price ?? currentItem.price ?? 0).toFixed(
              2
            ),
          }),
          ...(currentItem.category === "M" && {
            itemCode: varianceData.itemCode || currentItem.itemCode || "",
            itemDescription:
              varianceData.itemDescription || currentItem.itemDescription || "",
            size: varianceData.size || currentItem.size || "",
            container: varianceData.container || currentItem.container || "",
            bidArea: varianceData.bidArea || currentItem.bidArea || "",
            vendor: varianceData.vendor || currentItem.vendor?._id || null,
            requiredQty: Number(
              varianceData.requiredQty ?? currentItem.requiredQty ?? 0
            ),
            cost: Number(varianceData.cost ?? currentItem.cost ?? 0).toFixed(2),
            price: Number(varianceData.price ?? currentItem.price ?? 0).toFixed(
              2
            ),
          }),
        },
        quantityDiff: differences.quantityDiff,
        costDiff: differences.costDiff,
        priceDiff: differences.priceDiff,
      };

      setAllVariances((prev) => {
        const existingIndex = prev.findIndex(
          (v) => v.lineItemId === currentItem._id
        );
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = varianceEntry;
          return updated;
        }
        return [...prev, varianceEntry];
      });

      const checkAndProceed = () => {
        if (!isLastItem) {
          if (currentIndex < currentItems.length - 1) {
            const nextIndex = currentIndex + 1;
            const nextItem = currentItems[nextIndex];

            setAllVariances((currentAllVariances) => {
              const latestVariance = currentAllVariances.find(
                (v) => v.lineItemId === nextItem._id
              );
              setCurrentIndex(nextIndex);
              setVarianceData({
                varianceTitle: varianceData.varianceTitle,
                varianceDescription: varianceData.varianceDescription,
                ...(latestVariance?.updatedItem || {
                  itemCode: nextItem.itemCode || "",
                  itemDescription: nextItem.itemDescription || "",
                  size: nextItem.size || "",
                  container: nextItem.container || "",
                  bidArea: nextItem.bidArea || "",
                  vendor: nextItem.vendor?._id || null,
                  requiredQty: nextItem.requiredQty ?? 0,
                  cost: Number(nextItem.cost ?? 0).toFixed(2),
                  price: Number(nextItem.price ?? 0).toFixed(2),
                  costCodeDescription: nextItem.costCodeDescription || "",
                  uom: nextItem.uom || "",
                  quantity: nextItem.quantity ?? 0,
                  unitRate: Number(
                    nextItem.unitRate ||
                      (nextItem.quantity > 0
                        ? nextItem.amount / nextItem.quantity
                        : 0)
                  ).toFixed(2),
                  laborHours: nextItem.laborHours ?? 0,
                }),
              });
              return currentAllVariances;
            });
          } else if (newItems.length > 0 && !isProcessingNewItems) {
            setCurrentItems(newItems);
            setCurrentIndex(0);
            setIsProcessingNewItems(true);
            const nextItem = newItems[0];

            setAllVariances((currentAllVariances) => {
              const latestVariance = currentAllVariances.find(
                (v) => v.lineItemId === nextItem._id
              );
              setNewItems([]);
              setVarianceData({
                varianceTitle: varianceData.varianceTitle,
                varianceDescription: varianceData.varianceDescription,
                ...(latestVariance?.updatedItem || {
                  itemCode: nextItem.itemCode || "",
                  itemDescription: nextItem.itemDescription || "",
                  size: nextItem.size || "",
                  container: nextItem.container || "",
                  bidArea: nextItem.bidArea || "",
                  vendor: nextItem.vendor?._id || null,
                  requiredQty:
                    nextItem.varianceData?.requiredQty ??
                    nextItem.requiredQty ??
                    0,
                  cost: Number(
                    nextItem.varianceData?.cost ?? nextItem.cost ?? 0
                  ).toFixed(2),
                  price: Number(
                    nextItem.varianceData?.price ?? nextItem.price ?? 0
                  ).toFixed(2),
                  costCodeDescription:
                    nextItem.varianceData?.costCodeDescription ||
                    nextItem.costCodeDescription ||
                    "",
                  uom: nextItem.varianceData?.uom || nextItem.uom || "",
                  quantity:
                    nextItem.varianceData?.quantity ?? nextItem.quantity ?? 0,
                  unitRate: Number(
                    nextItem.varianceData?.unitRate ||
                      nextItem.unitRate ||
                      (nextItem.quantity > 0
                        ? nextItem.amount / nextItem.quantity
                        : 0)
                  ).toFixed(2),
                  laborHours:
                    nextItem.varianceData?.laborHours ??
                    nextItem.laborHours ??
                    0,
                }),
              });
              return currentAllVariances;
            });
          }
        } else {
          handleCreateVariance();
        }
      };

      if (
        currentItem?.category === "M" &&
        varianceData.size &&
        varianceData.size !== currentItem.size
      ) {
        setDialog({
          open: true,
          type: "warning",
          title: "Material Size Change Detected",
          message:
            "A change in material size has been detected. Please review and ensure that any associated labor items are updated accordingly.",
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            checkAndProceed();
          },
          onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      } else {
        checkAndProceed();
      }
    } else if (isLastItem) {
      handleCreateVariance();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0 || isProcessingNewItems) {
      const differences = calculateDifferences(currentItem, varianceData);
      const varianceEntry = {
        lineItemId: currentItem._id,
        updatedItem: {
          ...(currentItem.category === "L" && {
            costCodeDescription:
              varianceData.costCodeDescription ||
              currentItem.costCodeDescription ||
              "",
            uom: varianceData.uom || currentItem.uom || "",
            unitRate: Number(
              varianceData.unitRate ||
                currentItem.unitRate ||
                (currentItem.quantity > 0
                  ? currentItem.amount / currentItem.quantity
                  : 0)
            ).toFixed(2),
            quantity: Number(
              varianceData.quantity ?? currentItem.quantity ?? 0
            ),
            laborHours: Number(
              varianceData.laborHours ?? currentItem.laborHours ?? 0
            ),
            price: Number(varianceData.price ?? currentItem.price ?? 0).toFixed(
              2
            ),
          }),
          ...(currentItem.category === "M" && {
            itemCode: varianceData.itemCode || currentItem.itemCode || "",
            itemDescription:
              varianceData.itemDescription || currentItem.itemDescription || "",
            size: varianceData.size || currentItem.size || "",
            container: varianceData.container || currentItem.container || "",
            bidArea: varianceData.bidArea || currentItem.bidArea || "",
            vendor: varianceData.vendor || currentItem.vendor?._id || null,
            requiredQty: Number(
              varianceData.requiredQty ?? currentItem.requiredQty ?? 0
            ),
            cost: Number(varianceData.cost ?? currentItem.cost ?? 0).toFixed(2),
            price: Number(varianceData.price ?? currentItem.price ?? 0).toFixed(
              2
            ),
          }),
        },
        quantityDiff: differences.quantityDiff,
        costDiff: differences.costDiff,
        priceDiff: differences.priceDiff,
      };

      setAllVariances((prev) => {
        const existingIndex = prev.findIndex(
          (v) => v.lineItemId === currentItem._id
        );
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = varianceEntry;
          prev = updated;
        } else {
          prev = [...prev, varianceEntry];
        }

        let targetIndex, targetItems;
        if (isProcessingNewItems) {
          targetIndex = lineItems.length - 1;
          targetItems = lineItems;
          setIsProcessingNewItems(false);
        } else {
          targetIndex = currentIndex - 1;
          targetItems = currentItems;
        }

        const targetItem = targetItems[targetIndex];
        const latestVariance = prev.find(
          (v) => v.lineItemId === targetItem._id
        );

        setCurrentIndex(targetIndex);
        setCurrentItems(targetItems);
        setVarianceData({
          varianceTitle: varianceData.varianceTitle,
          varianceDescription: varianceData.varianceDescription,
          ...(latestVariance?.updatedItem || {
            itemCode: targetItem.itemCode || "",
            itemDescription: targetItem.itemDescription || "",
            size: targetItem.size || "",
            container: targetItem.container || "",
            bidArea: targetItem.bidArea || "",
            vendor: targetItem.vendor?._id || null,
            requiredQty: targetItem.requiredQty ?? 0,
            cost: Number(targetItem.cost ?? 0).toFixed(2),
            price: Number(targetItem.price ?? 0).toFixed(2),
            costCodeDescription: targetItem.costCodeDescription || "",
            uom: targetItem.uom || "",
            quantity: targetItem.quantity ?? 0,
            unitRate: Number(
              targetItem.unitRate ||
                (targetItem.quantity > 0
                  ? targetItem.amount / targetItem.quantity
                  : 0)
            ).toFixed(2),
            laborHours: targetItem.laborHours ?? 0,
          }),
        });

        return prev;
      });
    }
  };

  const handleCreateVariance = async () => {
    if (isSubmitting) return; // Prevent double-click
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found.");
      }

      // Validate variance data before sending
      if (currentItem && isLastItem) {
        if (
          currentItem.category === "M" &&
          varianceData.requiredQty === undefined
        ) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message: "Required Quantity is required for material line items.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }
        if (
          currentItem.category === "L" &&
          varianceData.quantity === undefined
        ) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message: "Quantity is required for labor line items.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }
      }

      const payload = {
        lineItems: allVariances.map((variance) => ({
          lineItemId: variance.lineItemId,
          updatedItem: variance.updatedItem,
          quantityDiff: variance.quantityDiff,
          priceDiff: variance.priceDiff,
          costDiff: variance.costDiff,
        })),
        varianceTitle: varianceData.varianceTitle || "",
        varianceDescription: varianceData.varianceDescription || "",
      };

      if (currentItem && isLastItem) {
        const differences = calculateDifferences(currentItem, varianceData);
        payload.lineItems.push({
          lineItemId: currentItem._id,
          updatedItem: {
            ...(currentItem.category === "L" && {
              costCodeDescription:
                varianceData.costCodeDescription ||
                currentItem.costCodeDescription ||
                "",
              uom: varianceData.uom || currentItem.uom || "",
              unitRate: Number(
                varianceData.unitRate ||
                  currentItem.unitRate ||
                  (currentItem.quantity > 0
                    ? currentItem.amount / currentItem.quantity
                    : 0)
              ).toFixed(2),
              quantity: Number(
                varianceData.quantity ?? currentItem.quantity ?? 0
              ),
              laborHours: Number(
                varianceData.laborHours ?? currentItem.laborHours ?? 0
              ),
              price: Number(
                varianceData.price ?? currentItem.price ?? 0
              ).toFixed(2),
            }),
            ...(currentItem.category === "M" && {
              itemCode: varianceData.itemCode || currentItem.itemCode || "",
              itemDescription:
                varianceData.itemDescription ||
                currentItem.itemDescription ||
                "",
              size: varianceData.size || currentItem.size || "",
              container: varianceData.container || currentItem.container || "",
              bidArea: varianceData.bidArea || currentItem.bidArea || "",
              vendor: varianceData.vendor || currentItem.vendor?._id || null,
              requiredQty: Number(
                varianceData.requiredQty ?? currentItem.requiredQty ?? 0
              ),
              cost: Number(varianceData.cost ?? currentItem.cost ?? 0).toFixed(
                2
              ),
              price: Number(
                varianceData.price ?? currentItem.price ?? 0
              ).toFixed(2),
            }),
          },
          quantityDiff: differences.quantityDiff,
          priceDiff: differences.priceDiff,
          costDiff: differences.costDiff,
        });
      }

      newItems.forEach((item) => {
        const differences = calculateDifferences(item, item.varianceData);
        payload.lineItems.push({
          lineItemId: item._id,
          updatedItem: {
            ...(item.category === "L" && {
              costCodeDescription:
                item.varianceData.costCodeDescription ||
                item.costCodeDescription ||
                "",
              uom: item.varianceData.uom || item.uom || "",
              unitRate: Number(item.varianceData.unitRate).toFixed(2),
              quantity: Number(item.varianceData.quantity ?? 0),
              laborHours: Number(
                item.varianceData.laborHours ?? item.laborHours ?? 0
              ),
              price: Number(item.varianceData.price ?? 0).toFixed(2),
            }),
            ...(item.category === "M" && {
              itemCode: item.varianceData.itemCode || item.itemCode || "",
              itemDescription:
                item.varianceData.itemDescription || item.itemDescription || "",
              size: item.varianceData.size || item.size || "",
              container: item.varianceData.container || item.container || "",
              bidArea: item.varianceData.bidArea || item.bidArea || "",
              vendor: item.varianceData.vendor || item.vendor?._id || null,
              requiredQty: Number(item.varianceData.requiredQty ?? 0),
              cost: Number(item.varianceData.cost ?? 0).toFixed(2),
              price: Number(item.varianceData.price ?? 0).toFixed(2),
            }),
          },
          quantityDiff: differences.quantityDiff,
          priceDiff: differences.priceDiff,
          costDiff: differences.costDiff,
        });
      });

      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}/budgets/${budgetId}/variance`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );

      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProject(updatedProjectResponse.data);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message:
          "Variance budget created successfully! Change order updated if applicable.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          onVarianceCreated(updatedProjectResponse.data);
          setIsSubmitting(false); // Unlock
        },
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to create variance budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          setIsSubmitting(false); // Unlock even on error
        },
      });
    }
  };

  const handleUpdateChangeOrder = async (projectData, lineItems) => {
    try {
      const token = localStorage.getItem("token");
      const budget = projectData.budgets.find((b) => b._id === budgetId);
      if (!budget?.isChangeOrder || !budget.changeOrderId) {
        return;
      }

      const changeOrder = projectData.changeOrders.find(
        (co) => co._id === budget.changeOrderId
      );
      if (!changeOrder) {
        throw new Error("Change order not found in project data.");
      }

      const updatedItems = changeOrder.items.map((item) => {
        const varianceItem = lineItems.find(
          (li) => li.lineItemId === item.budgetItemId
        );
        if (varianceItem) {
          return {
            ...item,
            quantity:
              varianceItem.updatedItem.quantity ||
              varianceItem.updatedItem.requiredQty ||
              item.quantity,
            unitCost:
              varianceItem.updatedItem.cost ||
              varianceItem.updatedItem.unitRate ||
              item.unitCost,
            price: varianceItem.updatedItem.price || item.price,
          };
        }
        return item;
      });

      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/change-orders/${changeOrder._id}`,
        { items: updatedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProject(updatedProjectResponse.data);
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update change order: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-[15000]">
      <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto border border-gray-600">
        <h3 className="text-lg font-semibold text-white mb-4">
          Create Variance{" "}
          {currentItems.length + newItems.length > 1
            ? `(${currentIndex + 1} of ${
                currentItems.length + newItems.length
              })`
            : ""}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Variance Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={varianceData.varianceTitle || ""}
              onChange={(e) =>
                handleVarianceChange("varianceTitle", e.target.value)
              }
              placeholder="Enter a title for this variance"
              className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              required
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Variance Reason <span className="text-red-400">*</span>
            </label>
            <textarea
              value={varianceData.varianceDescription || ""}
              onChange={(e) =>
                handleVarianceChange("varianceDescription", e.target.value)
              }
              placeholder="Explain why this variance is needed"
              className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              rows="2"
              required
            />
          </div>

          {showNewItemForm ? (
            <>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-400">
                  Select Item to Add
                </label>
                <Select
                  options={originalBudgetItems
                    .filter(
                      (item) =>
                        !lineItems.some((li) => li._id === item._id) &&
                        !newItems.some((ni) => ni._id === item._id)
                    )
                    .map((item) => ({
                      value: item._id,
                      label: `${item.costCodeDescription} (${item.category})`,
                    }))}
                  value={
                    newItemData.selectedItem
                      ? {
                          value: newItemData.selectedItem,
                          label: originalBudgetItems.find(
                            (item) => item._id === newItemData.selectedItem
                          )?.costCodeDescription,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleNewItemChange("selectedItem", selected?.value || null)
                  }
                  placeholder="Select an item"
                  className="w-full"
                  styles={customStyles}
                />
              </div>
              {newItemData.selectedItem && (
                <>
                  {originalBudgetItems.find(
                    (item) => item._id === newItemData.selectedItem
                  )?.category === "M" ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Item Code
                        </label>
                        <input
                          type="text"
                          value={newItemData.itemCode || ""}
                          onChange={(e) =>
                            handleNewItemChange("itemCode", e.target.value)
                          }
                          placeholder="Item Code"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Item Description
                        </label>
                        <input
                          type="text"
                          value={newItemData.itemDescription || ""}
                          onChange={(e) =>
                            handleNewItemChange(
                              "itemDescription",
                              e.target.value
                            )
                          }
                          placeholder="Item Description"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Size
                        </label>
                        <input
                          type="text"
                          value={newItemData.size || ""}
                          onChange={(e) =>
                            handleNewItemChange("size", e.target.value)
                          }
                          placeholder="Size"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Container
                        </label>
                        <input
                          type="text"
                          value={newItemData.container || ""}
                          onChange={(e) =>
                            handleNewItemChange("container", e.target.value)
                          }
                          placeholder="Container"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Bid Area
                        </label>
                        <Select
                          options={bidAreaOptions}
                          value={
                            bidAreaOptions.find(
                              (option) => option.value === newItemData.bidArea
                            ) || null
                          }
                          onChange={(selected) =>
                            handleNewItemChange(
                              "bidArea",
                              selected ? selected.value : ""
                            )
                          }
                          placeholder="Select Bid Area"
                          className="w-full"
                          styles={customStyles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Vendor
                        </label>
                        <Select
                          options={vendorOptions}
                          value={
                            vendorOptions.find(
                              (option) => option.value === newItemData.vendor
                            ) || null
                          }
                          onChange={(selected) =>
                            handleNewItemChange(
                              "vendor",
                              selected ? selected.value : null
                            )
                          }
                          placeholder="Select Vendor"
                          className="w-full"
                          styles={customStyles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Required Quantity
                        </label>
                        <input
                          type="number"
                          value={newItemData.requiredQty ?? ""}
                          onChange={(e) =>
                            handleNewItemChange(
                              "requiredQty",
                              Number(e.target.value)
                            )
                          }
                          placeholder="Required Quantity"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Cost
                        </label>
                        <NumericFormat
                          value={newItemData.cost ?? ""}
                          onValueChange={(values) =>
                            handleNewItemChange("cost", values.floatValue)
                          }
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          thousandSeparator={false}
                          placeholder="0.00"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Price
                        </label>
                        <NumericFormat
                          value={newItemData.price ?? ""}
                          onValueChange={(values) =>
                            handleNewItemChange("price", values.floatValue)
                          }
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          thousandSeparator={false}
                          placeholder="0.00"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Cost Code Description
                        </label>
                        <input
                          type="text"
                          value={newItemData.costCodeDescription || ""}
                          onChange={(e) =>
                            handleNewItemChange(
                              "costCodeDescription",
                              e.target.value
                            )
                          }
                          placeholder="Cost Code Description"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Unit of Measure
                        </label>
                        <input
                          type="text"
                          value={newItemData.uom || ""}
                          onChange={(e) =>
                            handleNewItemChange("uom", e.target.value)
                          }
                          placeholder="Unit of Measure"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={newItemData.quantity ?? ""}
                          onChange={(e) =>
                            handleNewItemChange(
                              "quantity",
                              Number(e.target.value)
                            )
                          }
                          placeholder="Quantity"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Unit Rate
                        </label>
                        <NumericFormat
                          value={newItemData.unitRate ?? ""}
                          onValueChange={(values) =>
                            handleNewItemChange("unitRate", values.floatValue)
                          }
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          thousandSeparator={false}
                          placeholder="0.00"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Labor Hours
                        </label>
                        <input
                          type="number"
                          value={newItemData.laborHours ?? ""}
                          onChange={(e) =>
                            handleNewItemChange(
                              "laborHours",
                              Number(e.target.value)
                            )
                          }
                          placeholder="Labor Hours"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Price
                        </label>
                        <NumericFormat
                          value={newItemData.price ?? ""}
                          onValueChange={(values) =>
                            handleNewItemChange("price", values.floatValue)
                          }
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          thousandSeparator={false}
                          placeholder="0.00"
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 flex justify-end space-x-2">
                    <button
                      onClick={() => setShowNewItemForm(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addNewItem}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                    >
                      Add Item
                    </button>
                  </div>
                </>
              )}
            </>
          ) : currentItem?.category === "M" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Item Code
                </label>
                <input
                  type="text"
                  value={varianceData.itemCode ?? currentItem.itemCode ?? ""}
                  onChange={(e) =>
                    handleVarianceChange("itemCode", e.target.value)
                  }
                  placeholder="Item Code"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.itemCode || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Item Description
                </label>
                <input
                  type="text"
                  value={
                    varianceData.itemDescription ??
                    currentItem.itemDescription ??
                    ""
                  }
                  onChange={(e) =>
                    handleVarianceChange("itemDescription", e.target.value)
                  }
                  placeholder="Item Description"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.itemDescription || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Size
                </label>
                <input
                  type="text"
                  value={varianceData.size ?? currentItem.size ?? ""}
                  onChange={(e) => handleVarianceChange("size", e.target.value)}
                  placeholder="Size"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.size || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Container
                </label>
                <input
                  type="text"
                  value={varianceData.container ?? currentItem.container ?? ""}
                  onChange={(e) =>
                    handleVarianceChange("container", e.target.value)
                  }
                  placeholder="Container"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.container || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Bid Area
                </label>
                <Select
                  options={bidAreaOptions}
                  value={
                    bidAreaOptions.find(
                      (option) =>
                        option.value ===
                        (varianceData.bidArea ?? currentItem.bidArea)
                    ) || null
                  }
                  onChange={(selected) =>
                    handleVarianceChange(
                      "bidArea",
                      selected ? selected.value : ""
                    )
                  }
                  placeholder="Select Bid Area"
                  className="w-full"
                  styles={customStyles}
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.bidArea || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Vendor
                </label>
                <Select
                  options={vendorOptions}
                  value={
                    vendorOptions.find(
                      (option) =>
                        option.value ===
                        (varianceData.vendor ?? currentItem.vendor?._id)
                    ) || null
                  }
                  onChange={(selected) =>
                    handleVarianceChange(
                      "vendor",
                      selected ? selected.value : null
                    )
                  }
                  placeholder="Select Vendor"
                  className="w-full"
                  styles={customStyles}
                />
                <p className="text-xs text-gray-400">
                  Original:{" "}
                  {vendorOptions.find(
                    (option) => option.value === currentItem.vendor?._id
                  )?.label || "Unassigned"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Required Quantity
                </label>
                <input
                  type="number"
                  value={
                    varianceData.requiredQty ?? currentItem.requiredQty ?? 0
                  }
                  onChange={(e) =>
                    handleVarianceChange("requiredQty", Number(e.target.value))
                  }
                  placeholder="Required Quantity"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.requiredQty || 0}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost
                </label>
                <NumericFormat
                  value={varianceData.cost ?? currentItem.cost ?? 0}
                  onValueChange={(values) =>
                    handleVarianceChange("cost", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: ${formatNumber(currentItem.cost || 0)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Price
                </label>
                <NumericFormat
                  value={varianceData.price ?? currentItem.price ?? 0}
                  onValueChange={(values) =>
                    handleVarianceChange("price", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: ${formatNumber(currentItem.price || 0)}
                </p>
              </div>
              <div className="col-span-2 border-t border-gray-600 pt-2">
                <p className="text-sm font-medium text-gray-400">
                  Variance Summary:
                </p>
                <p className="text-sm text-gray-400">
                  Quantity Difference: {quantityDiff}
                  <br />
                  Cost Difference: ${formatNumber(costDiff)}
                  <br />
                  Price Difference: ${formatNumber(priceDiff)}
                </p>
              </div>
            </>
          ) : currentItem?.category === "L" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost Code Description
                </label>
                <input
                  type="text"
                  value={
                    varianceData.costCodeDescription ??
                    currentItem.costCodeDescription ??
                    ""
                  }
                  onChange={(e) =>
                    handleVarianceChange("costCodeDescription", e.target.value)
                  }
                  placeholder="Cost Code Description"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.costCodeDescription || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit of Measure
                </label>
                <input
                  type="text"
                  value={varianceData.uom ?? currentItem.uom ?? ""}
                  onChange={(e) => handleVarianceChange("uom", e.target.value)}
                  placeholder="Unit of Measure"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.uom || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Quantity
                </label>
                <input
                  type="number"
                  value={varianceData.quantity ?? currentItem.quantity ?? 0}
                  onChange={(e) =>
                    handleVarianceChange("quantity", Number(e.target.value))
                  }
                  placeholder="Quantity"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.quantity || 0}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Rate
                </label>
                <NumericFormat
                  value={
                    varianceData.unitRate ??
                    (currentItem.unitRate ||
                      (currentItem.quantity > 0
                        ? currentItem.amount / currentItem.quantity
                        : 0))
                  }
                  onValueChange={(values) =>
                    handleVarianceChange("unitRate", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: $
                  {formatNumber(
                    currentItem.unitRate ||
                      (currentItem.quantity > 0
                        ? currentItem.amount / currentItem.quantity
                        : 0)
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Labor Hours
                </label>
                <input
                  type="number"
                  value={varianceData.laborHours ?? currentItem.laborHours ?? 0}
                  onChange={(e) =>
                    handleVarianceChange("laborHours", Number(e.target.value))
                  }
                  placeholder="Labor Hours"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: {currentItem.laborHours || 0}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Price
                </label>
                <NumericFormat
                  value={varianceData.price ?? currentItem.price ?? 0}
                  onValueChange={(values) =>
                    handleVarianceChange("price", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-400">
                  Original: ${formatNumber(currentItem.price || 0)}
                </p>
              </div>
              <div className="col-span-2 border-t border-gray-600 pt-2">
                <p className="text-sm font-medium text-gray-400">
                  Variance Summary:
                </p>
                <p className="text-sm text-gray-400">
                  Quantity Difference: {quantityDiff}
                  <br />
                  Cost Difference: ${formatNumber(costDiff)}
                  <br />
                  Price Difference: ${formatNumber(priceDiff)}
                </p>
              </div>
            </>
          ) : (
            <p className="col-span-2 text-sm text-gray-400">
              Unsupported category for variance
            </p>
          )}
          <div className="col-span-2 flex justify-between space-x-2">
            <button
              onClick={() => setShowNewItemForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              Add New Item
            </button>
            <div className="flex space-x-2">
              {(currentIndex > 0 || isProcessingNewItems) && (
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Back
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                  isSubmitting
                    ? "bg-orange-300 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500"
                }`}
              >
                {isSubmitting
                  ? "Creating..."
                  : isLastItem
                  ? "Create Variance"
                  : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VarianceModal;
